import { baseApi } from 'services/api';
import { API_TAG_TYPES, API_VERSION } from 'constants/common';
import {
  ApprovePayRunPayload,
  CreatePayRunNotePayload,
  DeletePayRunNotePayload,
  GetPayRunDetailsPayload,
  GetPayRunDetailsResponse,
  GetPayRunHistoryPayload,
  GetPayRunHistoryResponse,
  UpdatePayRunMerchantStatusBulkPayload,
  UpdatePayRunNotePayload
} from './payperiod.model';
import { getUrlSearchParams } from 'utils/apiUtils';

const PayPeriodReviewBaseApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getPayRunDetails: builder.query<GetPayRunDetailsResponse, GetPayRunDetailsPayload>({
      query: ({ teamId, payRunId, ...rest }) => {
        const urlParams = getUrlSearchParams(rest);
        return {
          url: `${API_VERSION.V1}/merchants/teams/${teamId}/pay-run/${payRunId}?${urlParams}`,
          method: 'GET'
        };
      },
      providesTags: [API_TAG_TYPES.PAY_PERIOD],
      extraOptions: {
        failure: 'Failed to fetch pay run details',
        showToast: true
      }
    }),
    getPayRunHistory: builder.query<GetPayRunHistoryResponse, GetPayRunHistoryPayload>({
      query: ({ teamId, payRunId }) => ({
        url: `${API_VERSION.V1}/team/${teamId}/pay-run/${payRunId}/history`,
        method: 'GET'
      }),
      providesTags: [API_TAG_TYPES.PAY_RUN_HISTORY],
      extraOptions: {
        failure: 'Failed to fetch pay run history',
        showToast: true
      }
    }),
    createPayRunNote: builder.mutation<string, CreatePayRunNotePayload>({
      query: ({ teamId, payRunId, merchantId, note }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/pay-run/${payRunId}/merchants/notes`,
        method: 'POST',
        body: {
          merchantId,
          note
        }
      }),
      invalidatesTags: [API_TAG_TYPES.PAY_RUN_HISTORY, API_TAG_TYPES.PAY_PERIOD],
      extraOptions: {
        failure: 'Failed to add note',
        success: 'Note added successfully',
        showToast: true
      }
    }),
    updatePayRunNote: builder.mutation<string, UpdatePayRunNotePayload>({
      query: ({ teamId, payRunId, merchantId, note }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/pay-run/${payRunId}/merchants/notes`,
        method: 'PUT',
        body: {
          merchantId,
          note
        }
      }),
      invalidatesTags: [API_TAG_TYPES.PAY_PERIOD, API_TAG_TYPES.PAY_RUN_HISTORY],
      extraOptions: {
        success: 'Note updated successfully',
        failure: 'Failed to update note',
        showToast: true
      }
    }),
    deletePayRunNote: builder.mutation<string, DeletePayRunNotePayload>({
      query: ({ teamId, payRunId, merchantId }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/pay-run/${payRunId}/merchants/${merchantId}/notes`,
        method: 'DELETE'
      }),
      invalidatesTags: [API_TAG_TYPES.PAY_PERIOD, API_TAG_TYPES.PAY_RUN_HISTORY],
      extraOptions: {
        success: 'Note deleted successfully',
        failure: 'Failed to delete note',
        showToast: true
      }
    }),
    updatePayRunMerchantStatusBulk: builder.mutation<void, UpdatePayRunMerchantStatusBulkPayload>({
      query: ({ teamId, payRunId, merchantIds, status }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/pay-run/${payRunId}/merchant/bulk-status`,
        method: 'PATCH',
        body: {
          merchantIds,
          status
        }
      }),
      invalidatesTags: ['PayPeriod']
    }),
    approvePayRun: builder.mutation<void, ApprovePayRunPayload>({
      query: ({ teamId, payRunId }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/pay-run/${payRunId}/approve`,
        method: 'POST'
      }),
      invalidatesTags: ['PayPeriod'],
      extraOptions: {
        success: 'Pay run approved successfully',
        failure: 'Failed to approve pay run',
        showToast: true
      }
    })
  })
});

export const {
  useLazyGetPayRunDetailsQuery,
  useCreatePayRunNoteMutation,
  useUpdatePayRunMerchantStatusBulkMutation,
  useUpdatePayRunNoteMutation,
  useApprovePayRunMutation,
  useDeletePayRunNoteMutation,
  useGetPayRunHistoryQuery
} = PayPeriodReviewBaseApi;
